import { createSelector, createSlice } from "@reduxjs/toolkit";
import { getLeague, getTeams, getTournaments } from "./clubhouseActions";
import { IStateDefaults } from "../../types/shared";
import { ITeam, ITournamentList } from "../../types/api";
import { authActions } from "../auth/authSlice";
import { RootState } from "../../store";

interface ClubhouseState extends IStateDefaults {
    teams: ITeam[];
    tournaments: ITournamentList[];
    selectedTeam?: ITeam;
    selectedTournamentTeam?: ITeam["id"];
}

const initialState: ClubhouseState = {
    teams: [],
    tournaments: [],
    selectedTeam: undefined,
    selectedTournamentTeam: undefined,
    loading: false,
    success: false,
};

const clubhouseSlice = createSlice({
    name: "clubhouse",
    initialState,
    reducers: {
        setSelectedTeam: (state, action) => {
            state.selectedTeam = action.payload;
        },
        setSelectedLeagueTeam: (state, action) => {
            state.selectedTournamentTeam = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(authActions.logout, state => {
                state.teams = [];
                state.tournaments = [];
                state.selectedTeam = undefined;
                state.selectedTournamentTeam = undefined;
            })
            .addCase(getTeams.pending, state => {
                state.loading = true;
            })
            .addCase(getTeams.fulfilled, (state, { payload }) => {
                const allTeams = payload.teams.filter(t => t !== null);

                const teamIds = new Set(allTeams.map(t => t.id));

                const uniqueTeams = Array.from(teamIds).map(id => {
                    return allTeams.find(t => t.id === id) as ITeam;
                });

                if (uniqueTeams.length === 1) {
                    state.selectedTeam = uniqueTeams[0];
                }
                state.teams = uniqueTeams;
                state.loading = false;
            })
            .addCase(getTeams.rejected, state => {
                state.loading = false;
            })
            .addCase(getTournaments.pending, state => {
                state.loading = true;
            })
            .addCase(getTournaments.fulfilled, (state, { payload }) => {
                const tournaments = payload.tournaments;

                tournaments.sort((a, b) => {
                    const dateA = new Date(a.startDate || 0).getTime();
                    const dateB = new Date(b.startDate || 0).getTime();

                    return dateA - dateB;
                });

                tournaments.map(t => {
                    if (t.teams) {
                        t.teams.sort((a, b) => {
                            return a.name.localeCompare(b.name);
                        });
                    }
                });

                state.teams = [
                    ...new Set(
                        tournaments.flatMap(t => t.teams).filter(t => t),
                    ),
                ];

                state.tournaments = tournaments;
                state.loading = false;
            })
            .addCase(getTournaments.rejected, state => {
                state.loading = false;
            });
    },
});

export const clubhouseActions = {
    ...clubhouseSlice.actions,
    getTournaments: getTournaments,
    getTeams: getTeams,
};

export default clubhouseSlice.reducer;

export const selectSelectedTeam = createSelector(
    [(state: RootState) => state.clubhouse],
    clubhouse => clubhouse.selectedTeam,
    // clubhouse => {
    //     const selectedTeam = clubhouse.selectedTeam;
    //     if (selectedTeam) {
    //         const team = clubhouse.teams.find(team => team.id === selectedTeam);
    //         return team;
    //     }
    //     return undefined;
    // },
);

export const selectSelectedTournamentTeam = createSelector(
    [(state: RootState) => state.clubhouse],
    clubhouse => clubhouse.selectedTournamentTeam,
);

export const selectClubhouseTeams = createSelector(
    [(state: RootState) => state.clubhouse],
    clubhouse => clubhouse.teams,
);

export const selectClubhouseTournaments = createSelector(
    [(state: RootState) => state.clubhouse],
    clubhouse => clubhouse.tournaments,
);
